import { dayjs, ElForm, ElFormItem, ElSkeleton, ElSkeletonItem, ElSwitch } from 'element-plus'
import './FinancePositionDetail.scss'
import { useI18n } from 'i18n-utils'
import { ApiStatus, InvestFinancialService } from 'network'
import successImg from 'platform-config/src/assets/common/financial/success.png'
import warnImg from 'platform-config/src/assets/common/financial/warn.png'
import { getUserCenterPageRoute, PageRoutes, UserCenterRoutes } from 'platform-config/src/router/PageRoutes'
import { InvestFinancialTab, InvestFinancialTabTypes, useResponsive, useStaticTableDataStore } from 'store'
import { safeSecondToMillisecond } from 'utils'
import type { FunctionalComponent } from 'vue'
import { CustomButton } from '../components/Button'
import { CommonCell } from '../components/CommonCell'
import { DialogHeader } from '../components/DialogHeader'
import { InputView } from '../components/InputView'
import { HocElDialog, HocElDialogFooterLayout } from '../hoc-element-components/HocElDialog'
import { HocElDrawer } from '../hoc-element-components/HocElDrawer'
import { useDialog } from '../stores/use-dialog'
import { UiUtils } from '../ui-utils/ui-utils'

export type FEFinancePositionDetailProps = {
  visible: boolean
  positionId: number
  itemId: ItemId
  isSpecial: boolean
  onClose?: () => void
  mobileIsDialog?: boolean
}


type FinancePositionDetailSkeletonProps = {
  loading: boolean
}

const FinancePositionDetailSkeleton: FunctionalComponent<FinancePositionDetailSkeletonProps> = (props, { slots }) => {
  if (props.loading) {
    return (
      <div class="finance-position-detail-skeleton-layout">
        <ElSkeleton animated loading>
          {{
            template: () => (
              <>
                <div class={'coin-info flex items-center gap-2 border-b border-[var(--colors-card-border)] pb-2'}>
                  <ElSkeletonItem variant={'rect'} class={'icon-skeleton'} />

                  <ElSkeletonItem variant={'rect'} class={'name-skeleton'} />
                </div>
              </>
            ),
          }}
        </ElSkeleton>
        <div class={'mt-4'}>
          <ElSkeleton animated loading rows={5}></ElSkeleton>
        </div>
      </div>
    )
  }
  return slots.default?.()
}
export const FinancePositionDetail: FunctionalComponent<FEFinancePositionDetailProps> = (props) => {
  const visible = ref(false)
  const responsive = useResponsive()
  const { t } = useI18n()
  const staticTableStore = useStaticTableDataStore()
  if (props.visible) {
    visible.value = true
  }

  const autoBuyLoading = ref(false)
  const positionDetailLoading = ref(true)

  const positionDetail = ref<InvestFinancialResponse.PositionDetail>()
  const autoSubscriptionProductList = ref<InvestFinancialResponse.AutoSubscriptionProductItem[]>([])
  /** 活期赎回金额 */
  const periodRedeemAmount = ref('')
  /** 活期赎回loading */
  const periodRedeemLoading = ref(false)

  const Wrapper = props.mobileIsDialog ? (responsive.isMobile ? HocElDialog : HocElDrawer) : HocElDrawer

  const { dialogVisible: activeRedeemDialog, openDialog: openActiveRedeemDialog, closeDialog: closeActiveRedeemDialog } = useDialog()

  const onClose = () => {
    visible.value = false
    props.onClose?.()
  }

  const fetchAutoSubscriptionProductList = async () => {
    const res = await InvestFinancialService.getAutoSubscriptionProducts()
    if (res.status === ApiStatus.Success) {
      autoSubscriptionProductList.value = res.data
    }
  }

  const fetchPositionDetail = async () => {
    const res = await InvestFinancialService.getPositionDetail({ positionId: props.positionId })
    if (res.status === ApiStatus.Success) {
      positionDetail.value = res.data
    }
    positionDetailLoading.value = false
  }

  fetchPositionDetail()

  fetchAutoSubscriptionProductList()

  const coinUrl = computed(() => {
    return staticTableStore.getItemInfoByItemId(props.itemId)?.itemDetail.coinUrl
  })

  const itemName = computed(() => {
    return staticTableStore.getItemInfoByItemId(props.itemId)?.itemName.toUpperCase()
  })

  const suffixName = computed(() => {
    if (positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Fixed]) {
      return '-' + t('{days}天', { days: positionDetail.value?.productLockDay })
    }
    return ''
  })

  const positionStatusText = computed(() => {
    let text = ''
    switch (positionDetail.value?.status) {
      case 1:
        text = t('待计息')
        break
      case 2:
        text = t('计息中')
        break
      case 3:
        text = t('赎回中')
        break
      case 4:
        text = t('本息已发放')
        break
      default:
        break
    }
    return text
  })

  /** 是否支持自动申购 */
  const isAutoBuyFinancial = computed(() => {
    return positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Period]
  })

  /** 是否开启了自动申购 */
  const isAutoBuy = computed(() => {
    return autoSubscriptionProductList.value.some((item) => item.subProductId === positionDetail.value?.subProductId)
  })

  const onToggleAutoBuy = async () => {
    autoBuyLoading.value = true
    const res = await InvestFinancialService.feAutoSubscription({
      subProductId: positionDetail.value!.subProductId,
      autoBuy: isAutoBuy.value ? 2 : 1,
    })
    autoBuyLoading.value = false
    if (res.status !== ApiStatus.Success) {
      return
    }

    if (isAutoBuy.value) {
      UiUtils.showSuccess(t('自动申购已关闭'))
      const index = autoSubscriptionProductList.value.findIndex((item) => item.subProductId === positionDetail.value?.subProductId)
      if (index !== -1) {
        autoSubscriptionProductList.value.splice(index, 1)
      }
    } else {
      UiUtils.showSuccess(t('自动申购已开启'))
      const hasProduct = autoSubscriptionProductList.value.some((item) => item.subProductId === positionDetail.value?.subProductId)
      if (!hasProduct) {
        autoSubscriptionProductList.value.push({
          subProductId: positionDetail.value!.subProductId,
          productId: positionDetail.value!.productId,
        })
      }
    }
    await fetchAutoSubscriptionProductList()
    autoBuyLoading.value = false
  }

  /**赎回按钮文案 */
  const redeemButtonText = computed(() => {
    // 活期叫赎回，定期叫提前赎回
    if (positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Period]) {
      return t('赎回')
    }
    return t('提前赎回')
  })

  const earlyRdmReceivedTimeText = computed(() => {
    const earlyRdmReceivedDay = positionDetail.value?.earlyRdmReceivedDay ?? 0
    const noEarlyRdmReceivedDay = earlyRdmReceivedDay === 0
    return noEarlyRdmReceivedDay ? t('1 小时内') : t('{days}天', { days: earlyRdmReceivedDay })
  })

  /** 是否允许赎回 */
  const isAllowRedeem = computed(() => {
    const lockDay = positionDetail.value?.lockDay ?? 0
    if (lockDay !== 0) {
      return positionDetail.value?.isEalryRdm === 1
    }
    return positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Period]
  })

  const onShowRedeemSuccessDialog = () => {
    UiUtils.showModal(
      <div>
        <div class="flex flex-col items-center justify-center">
          <img src={successImg} width={60} height={60} alt="success" />
          <div class="mt-8 text-[18px] font-[500] leading-[21px]">{t('赎回已发起')}</div>
          <div class="mt-3 text-[14px] font-[400] leading-[22px] text-[var(--colors-text-secondary)]">
            {t('预计到账时间{time}', {
              time: earlyRdmReceivedTimeText.value,
            })}
          </div>
        </div>
      </div>,
      {
        cancelButtonText: t('查看订单'),
        confirmButtonText: t('前往账单'),
        width: '440px',
        onCancel: () => {
          visible.value = false
          props.onClose?.()
          UiUtils.router?.push({
            path: getUserCenterPageRoute(UserCenterRoutes.FinancialHistory),
            query: {
              productType: positionDetail.value?.productType,
            },
          })
        },
        onConfirm: () => {
          visible.value = false
          props.onClose?.()
          UiUtils.router?.push({
            path: getUserCenterPageRoute(UserCenterRoutes.AssetBill),
          })
        },
      },
    )
  }

  const onRedeem = () => {
    const productType = positionDetail.value?.productType ?? 0
    // 定期 量化理财 链上赚币 则直接赎回，赎回成功即弹窗
    if (
      [
        InvestFinancialTabTypes[InvestFinancialTab.Fixed],
        InvestFinancialTabTypes[InvestFinancialTab.ChainEarn],
        InvestFinancialTabTypes[InvestFinancialTab.QuantitativeFinance],
      ].includes(productType)
    ) {
      UiUtils.showModal(
        <div class="flex flex-col items-center justify-center">
          <img src={warnImg} width={60} height={60} alt="warn" />
          <p class="mt-4">
            {t('提前赎回将以 {smallRate}% 计算收益，或者持有到期后以 {largeRate}% 计算收益', {
              smallRate: positionDetail.value?.earlyRdmAnnualRate.decimalPadZero(2),
              largeRate: positionDetail.value?.annualRate.decimalPadZero(2),
            })}
          </p>
        </div>,
        {
          confirmButtonText: t('提前赎回'),
          cancelButtonText: t('取消赎回'),
          width: '440px',
          onConfirm: async () => {
            const res = await InvestFinancialService.feFinanceRedemption({ positionId: props.positionId })
            if (res.status === ApiStatus.Success) {
              onShowRedeemSuccessDialog()
            }
          },
        },
      )
    } else {
      openActiveRedeemDialog()
    }
  }

  const onPeriodRedeem = async () => {
    const amount = periodRedeemAmount.value.trim() || '0'
    if (amount.isEqualTo(0)) {
      throw UiUtils.showError(t('请输入赎回金额'))
    }
    periodRedeemLoading.value = true
    const res = await InvestFinancialService.feFinanceRedemption({ positionId: props.positionId, amount: periodRedeemAmount.value })
    periodRedeemLoading.value = false
    if (res.status === ApiStatus.Success) {
      closeActiveRedeemDialog()
      onShowRedeemSuccessDialog()
    }
  }

  const onContinuePurchase = () => {
    visible.value = false
    props.onClose?.()
    UiUtils.router?.push({
      path: PageRoutes.InvestProductDetail,
      query: {
        productId: positionDetail.value?.productId,
        productType: positionDetail.value?.productType,
        subProductId: positionDetail.value?.subProductId,
        itemId: positionDetail.value?.itemId,
      },
    })
  }

  return (
    <div class="finance-position-detail-layout">
      <Wrapper v-model={visible.value} showClose={false} size={responsive.isMobile ? '100%' : '550px'} offsetTop={64} onClose={onClose}>
        {{
          header: () => <DialogHeader onClose={onClose}>{t('资产')}</DialogHeader>,
          default: () => (
            <FinancePositionDetailSkeleton loading={positionDetailLoading.value}>
              <div class={'coin-info flex items-center gap-2 border-b border-[var(--colors-card-border)] pb-3'}>
                <img width={32} src={coinUrl.value} alt="" />
                <span class={'text-[16px] font-[500]'}>
                  {positionDetail.value?.productName}
                  {suffixName.value}
                </span>
              </div>
              <div class="mt-4">
                <div class={'text-[13px] font-[400] text-[var(--colors-text-secondary)]'}>{t('数量')}</div>
                <div class={'text-[20px] font-[500]'}>
                  {positionDetail.value?.amount.formatCommonMoney()} {itemName.value}
                </div>
              </div>
              <div class="mt-4">
                <CommonCell title={t('参考年化')}>{positionDetail.value?.annualRate.decimalPadZero(2)}%</CommonCell>
                {positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Period] && (
                  <>
                    <CommonCell title={t('昨日活期收益')}>
                      {positionDetail.value.lastEarnings.formatCommonMoney()} {itemName.value}
                    </CommonCell>
                    <CommonCell title={t('累计活期收益')}>
                      {positionDetail.value.totalEarnings.formatCommonMoney()} {itemName.value}
                    </CommonCell>
                  </>
                )}

                {positionDetail.value?.productType === InvestFinancialTabTypes[InvestFinancialTab.Fixed] && (
                  <>
                    <CommonCell title={t('申购日期')}>{dayjs(safeSecondToMillisecond(positionDetail.value?.createTime)).format('YYYY-MM-DD HH:mm:ss')}</CommonCell>
                  </>
                )}
                {positionDetail.value?.productType !== InvestFinancialTabTypes[InvestFinancialTab.Period] && (
                  <>
                    <CommonCell title={t('锁仓期限')}>
                      {positionDetail.value?.productLockDay} {t('天')}
                    </CommonCell>
                    <CommonCell title={t('计息结束日期')}>{dayjs(safeSecondToMillisecond(positionDetail.value?.interestEndTime)).format('YYYY-MM-DD HH:mm:ss')}</CommonCell>
                  </>
                )}
                {[InvestFinancialTabTypes[InvestFinancialTab.ChainEarn], InvestFinancialTabTypes[InvestFinancialTab.QuantitativeFinance]].includes(
                  positionDetail.value?.productType ?? 0,
                ) && (
                  <>
                    <CommonCell title={t('到期收益')}>
                      {positionDetail.value?.maturityAmount.formatCommonMoney()} {itemName.value}
                    </CommonCell>
                  </>
                )}

                {positionDetail.value?.lockedDay !== 0 && (
                  <>
                    <CommonCell title={t('计息天数')}>{t('{days}天', { days: positionDetail.value?.lockedDay })}</CommonCell>
                  </>
                )}

                {positionStatusText.value && <CommonCell title={t('持仓状态')}>{positionStatusText.value}</CommonCell>}
              </div>

              {isAutoBuyFinancial.value && (
                <div class={'bg-[var(--colors-bg-main-page-card)] rounded-[8px] p-4 mt-4 flex items-center justify-between'}>
                  <span>{t('自动申购')}</span>
                  <ElSwitch modelValue={isAutoBuy.value} loading={autoBuyLoading.value} onChange={onToggleAutoBuy} />
                </div>
              )}
            </FinancePositionDetailSkeleton>
          ),
          footer: () => {
            if (positionDetailLoading.value) {
              return null
            }
            return (
              <HocElDialogFooterLayout>
                {isAllowRedeem.value && (
                  <CustomButton type="default" isFull size={'medium'} onClick={onRedeem}>
                    {redeemButtonText.value}
                  </CustomButton>
                )}

                {/* 活期和定期才有继续申购 */}
                {[InvestFinancialTabTypes[InvestFinancialTab.Period], InvestFinancialTabTypes[InvestFinancialTab.Fixed]].includes(positionDetail.value?.productType ?? 0) && (
                  <CustomButton type="primary" isFull size={'medium'} onClick={onContinuePurchase}>
                    {t('继续申购')}
                  </CustomButton>
                )}
              </HocElDialogFooterLayout>
            )
          },
        }}
      </Wrapper>
      {/* 活期赎回 */}
      <HocElDialog v-model={activeRedeemDialog.value} width={440} placement="bottom" showClose={false} onClose={closeActiveRedeemDialog} closeOnClickModal={false}>
        {{
          header: () => <DialogHeader onClose={closeActiveRedeemDialog}>{positionDetail.value?.productName}</DialogHeader>,
          default: () => (
            <ElForm labelPosition="top">
              <ElFormItem label={t('赎回金额')}>
                <InputView v-model={periodRedeemAmount.value} isFloatNumber placeholder={t('请输入赎回金额')} />
                <p>{t('当前存款：{amount} {itemName}', { amount: positionDetail.value?.amount.formatCommonMoney(), itemName: itemName.value })}</p>
              </ElFormItem>
            </ElForm>
          ),
          footer: () => (
            <HocElDialogFooterLayout>
              <CustomButton type="primary" isFull size={'medium'} loading={periodRedeemLoading.value} onClick={onPeriodRedeem}>
                {t('确认')}
              </CustomButton>
            </HocElDialogFooterLayout>
          ),
        }}
      </HocElDialog>
    </div>
  )
}
